import { createElement, type ComponentType } from "react";

/**
 * Custom lazy-component helper for code-split route pages.
 *
 * Why not React.lazy?
 *   The site is prerendered via `renderToString`, which doesn't await Suspense
 *   boundaries — any unresolved React.lazy would emit only its fallback HTML.
 *   This wrapper exposes a `preload()` method so the prerender step can await
 *   every page's chunk before rendering, producing complete static HTML while
 *   still letting the client download chunks per route.
 *
 *   On the client, the component throws its loading promise to suspend, which
 *   the surrounding <Suspense fallback={null}> boundary handles during route
 *   transitions.
 */
export function lazyPage<P extends object>(
  loader: () => Promise<{ default: ComponentType<P> }>,
) {
  let Resolved: ComponentType<P> | null = null;
  let pending: Promise<void> | null = null;

  const preload = (): Promise<void> => {
    if (Resolved) return Promise.resolve();
    if (!pending) {
      pending = loader().then((mod) => {
        Resolved = mod.default;
      });
    }
    return pending;
  };

  const Lazy: ComponentType<P> = (props: P) => {
    if (!Resolved) {
      // Suspend until preload() resolves and Resolved is populated.
      throw preload();
    }
    return createElement(Resolved, props);
  };
  Lazy.displayName = "LazyPage";

  return Object.assign(Lazy, { preload });
}

export type LazyPageComponent<P extends object = object> = ReturnType<
  typeof lazyPage<P>
>;
